<template>
  <header class="package-builder-header">
    <h1
      :id="headingId"
      class="package-builder-header__bundle-name"
    >
      {{ bundleName }}
    </h1>
    <div
      v-if="bundleDescription"
      class="package-builder-header__bundle-description"
      v-html="bundleDescription"
    />
    <div class="package-builder-header__sheet-label">
      <span class="package-builder-header__sheet-label-heading">{{ heading }}</span>
      <span class="package-builder-header__sheet-label-count">{{ subtitle }}</span>
    </div>
  </header>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

import { useAppContentStore } from '@/stores/appContent'
import { usePackageBuilderStore } from '@/stores/packageBuilder'
import { mapState, mapStores } from 'pinia'

import type { Document } from '@contentful/rich-text-types'

import { renderRichTextToHtml } from '@/composables/contentful'

import {
  type BuilderContent,
  resolveBuilderContent,
  substitute
} from '@/components/Senior/PackageBuilder/content'

export default defineComponent({
  name: 'PackageBuilderHeader',
  props: {
    // Passed in by `PackageBuilderModal` so the heading's `id` is unique per
    // modal instance and pairs with the dialog's `aria-labelledby`.
    headingId: { type: String, required: true },
    // The cart bundle being edited in the modal.
    cartBundleId: { type: String, required: true }
  },
  computed: {
    ...mapStores(useAppContentStore),
    ...mapState(usePackageBuilderStore, ['catalogBundleForCartBundle', 'targetCountForCartBundle']),
    content(): BuilderContent {
      return resolveBuilderContent(this.appContentStore.seniorPackageBuilderContent)
    },
    bundleName(): string {
      return this.catalogBundleForCartBundle(this.cartBundleId)?.content?.name ?? ''
    },
    bundleDescription(): string | null {
      const doc = this.catalogBundleForCartBundle(this.cartBundleId)?.content?.cartDescription as
        | Document
        | undefined
      return doc ? renderRichTextToHtml(doc) : null
    },
    heading(): string {
      return this.content.modalHeading
    },
    subtitle(): string {
      return substitute(this.content.instructions, {
        PackageTargetCount: String(this.targetCountForCartBundle(this.cartBundleId))
      })
    }
  }
})
</script>

<style scoped lang="scss">
.package-builder-header {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;

  &__bundle-name {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
    color: #000;
  }

  &__bundle-description {
    margin: 0;
    color: #555;
    font-size: 0.9rem;

    :deep(p) {
      margin: 0;
    }
  }

  &__sheet-label {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    margin-top: 0.25rem;
  }

  &__sheet-label-heading {
    font-size: 1rem;
    font-weight: 700;
    color: #000;
  }

  &__sheet-label-count {
    font-size: 0.9rem;
    color: #555;
  }

  @include media-breakpoint-up(md) {
    &__bundle-name {
      font-size: 1.5rem;
    }
  }
}
</style>
